import React, {FC, Fragment} from "react";
import {ExternalChatMessage, ExternalChatUser} from "domain-entities";
import {Box, makeStyles} from "@material-ui/core";
import {color, px} from "Configs/theme";
import {getColorRgbaFromHex} from "Helpers/ColorHelper";
import moment from "moment";
import {useTranslation} from "Providers/TranslationProvider";
import {Skeleton} from "@material-ui/lab";
import DescriptionIcon from "@material-ui/icons/Description";
import FadeInBox from "Components/Layout/FadeInBox";
import {isImageMimeType} from "Helpers/AssetHelper";
import Linkify from "Components/Layout/Linkify";
import {useDownloadMessageMedia, useMessageMedia} from "./MessageHelper";
import {SingleTickIcon} from "../Icons/SingleTickIcon";
import {DoubleTickIcon} from "../Icons/DoubleTickIcon";

interface Props {
  message: ExternalChatMessage;
  viewerUser: ExternalChatUser;
  senderUser?: ExternalChatUser;
  openGalleryModal: () => void;
}

const MessageView: FC<Props> = (props) => {
  const {t} = useTranslation();
  const downloadMessageMedia = useDownloadMessageMedia();
  const classes = useStyles();
  const messageMedia = useMessageMedia(props.message, MIN_THUMBNAIL_SIZE);
  const direction = props.viewerUser.id === props.message.sender ? "right" : "left";
  const messageClass = `${classes.message} ${
      props.message?.media ? 'media' : ''
  }`;

  async function openMedia() {
    if (props.message.media) {
      if (isImageMimeType(props.message.media.mimeType)) {
        props.openGalleryModal();
      } else {
        downloadMessageMedia(props.message);
      }
    }
  }

  function renderSenderName() {
    if (direction === "left" && props.senderUser?.name) {
      return `${props.senderUser?.name} - `;
    }
  }

  function renderMessageSentStatus() {
    if (direction === "right" && props.message?.state) {
      return (<Box className={classes.sendStatus}>
        {
          props.message?.state === "sent"
              ? <SingleTickIcon fontSize="small"/>
              : <DoubleTickIcon fontSize="small"/>
        }
      </Box>)
    }
  }

  function renderMedia() {
    if (props.message.media) {
      if (isImageMimeType(props.message.media.mimeType)) {
        if (messageMedia.url) {
          return (
              <FadeInBox
                  onClick={openMedia}
                  delayIndex={1}
                  className={classes.imageFadeBox}
              >
                <img
                    className={classes.image}
                    alt={props.message.media.name}
                    src={messageMedia.url}
                />
              </FadeInBox>
          );
        } else {
          return (
            <FadeInBox delayIndex={1}>
              <Skeleton
                className={classes.image}
                variant="rect"
                width={IMAGE_SIZE}
                height={IMAGE_SIZE}
              />
            </FadeInBox>
          );
        }
      } else {
        return (
          <FadeInBox
            onClick={openMedia}
            className={`${classes.message} ${classes.messageFile}`}
          >
            <DescriptionIcon style={{ fontSize: px.xxxl }} />
            <Box>{props.message.media.name}</Box>
          </FadeInBox>
        );
      }
    }
  }

  return (
    <Fragment>
      <Box textAlign={direction} className={classes.timestamp}>
        {renderSenderName()}
        {moment(props.message.timestamp).format(t("Common_Format_Time"))}
      </Box>
      <Box className={`${classes.messageContainer} ${direction}`}>
        <FadeInBox className={messageClass}>
          {props.message.text
              ? <Linkify text={props.message.text}/>
              : renderMedia()}
          {renderMessageSentStatus()}
        </FadeInBox>
      </Box>
    </Fragment>
  );
};

export default MessageView;

const IMAGE_SIZE = 256;
const MIN_THUMBNAIL_SIZE = 512;

const useStyles = makeStyles({
  timestamp: {
    fontSize: "12px",
    color: getColorRgbaFromHex(color.text, 0.5),
    marginBottom: px.xxs,
  },
  imageFadeBox: {
    display: "flex",
  },
  image: {
    cursor: "pointer",
    height: IMAGE_SIZE,
    borderRadius: px.xs,
    marginBottom: px.l,
    "&:hover": {
      opacity: 0.8,
    },
  },
  messageContainer: {
    display: "flex",
    "&.left": {
      "& > $message": {
        borderBottomRightRadius: px.s,
        backgroundColor: getColorRgbaFromHex(color.text, 0.1),
        "& > a": {
          color: color.primary,
        },
      },
    },
    "&.right": {
      justifyContent: "flex-end",
      "& > $message": {
        color: color.card,
        borderBottomLeftRadius: px.s,
        backgroundColor: color.primary,
        "& > a": {
          color: color.card,
        },
      },
    },
  },
  message: {
    display: "flex",
    maxWidth: "70%",
    borderRadius: px.xs,
    fontSize: "14px",
    lineHeight: "22px",
    fontWeight: 500,
    padding: `${px.s} ${px.m}`,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    marginBottom: px.l,
    "&.media": {
      flexDirection: "column"
    }
  },
  messageFile: {
    cursor: "pointer",
    alignItems: "center",
    textDecorationLine: "underline",
    "&:hover": {
      opacity: 0.8,
    },
  },
  sendStatus: {
    marginLeft: px.s,
    fontSize: px.s,
    alignSelf: "flex-end"
  }
});
